import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRecordContext } from "ra-core";
import { useNavigate } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useSwipe } from "../misc/useSwipe";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Contact } from "../types";

// LDF: Auf der Kontaktseite zwischen den Kontakten DERSELBEN Firma blättern (Buttons + Pfeiltasten ← →).

export const ContactCompanyPrevNext = ({ withSwipeHint = false }: { withSwipeHint?: boolean }) => {
  const record = useRecordContext<Contact>();
  const navigate = useNavigate();
  const companyId = record?.company_id;

  const { data: ids } = useQuery({
    queryKey: ["company-contact-ids", companyId],
    enabled: companyId != null,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("contacts")
        .select("id")
        .eq("company_id", companyId!)
        .order("last_name", { ascending: true })
        .order("first_name", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((c) => c.id as number);
    },
  });

  const index = ids && record ? ids.indexOf(Number(record.id)) : -1;
  const prevId = index > 0 ? ids![index - 1] : null;
  const nextId = ids && index >= 0 && index < ids.length - 1 ? ids[index + 1] : null;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable=true], [role=dialog], [role=tablist], [role=listbox], [role=menu]")) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft" && prevId) navigate(`/contacts/${prevId}/show`);
      if (e.key === "ArrowRight" && nextId) navigate(`/contacts/${nextId}/show`);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prevId, nextId, navigate]);

  // Handy: wischen blättert durch die Kontakte derselben Firma
  useSwipe({
    onLeft: () => nextId && navigate(`/contacts/${nextId}/show`),
    onRight: () => prevId && navigate(`/contacts/${prevId}/show`),
  });

  if (!ids || ids.length < 2 || index < 0) return null;

  const controls = (
    <div className="flex items-center gap-1 text-xs text-muted-foreground" title="Mit ← → zwischen den Kontakten dieser Firma blättern">
      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!prevId} onClick={() => prevId && navigate(`/contacts/${prevId}/show`)}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="tabular-nums">
        {index + 1} / {ids.length}
      </span>
      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={!nextId} onClick={() => nextId && navigate(`/contacts/${nextId}/show`)}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );

  // Handy: Hinweis nur zeigen, wenn es wirklich weitere Kontakte der Firma gibt
  return withSwipeHint ? (
    <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
      <span>← wischen: Kontakte dieser Firma →</span>
      {controls}
    </div>
  ) : (
    controls
  );
};
